/**
 * ***********************************************************************
 * Responsibility - Provides the disapproval flow for the design proofing
 * ***********************************************************************
 *
 * @format
 */

import {SelectWithErrorHandler, TextAreaWithErrorHandler} from 'frontier-form';
import {Modal, ModalFooter, ModalHeader, Toast} from '@sttark/design-system';
import {getDisapproveReasons} from '../utils/get_disaprove_reasons';
import {useProofingContext} from '../proofing_context_provider';
import {FileUploader} from '../utils/file_uploader';
import {IfElse, If} from 'frontier-utilities';
import {
    DESIGN_DISAPPROVE_REASON_CHANGE_ARTWORK,
    DESIGN_DISAPPROVE_REASON_CHANGE_ORDER,
    DESIGN_DISAPPROVE_REASON_NEW_ARTWORK,
    DESIGN_STATUS_DISAPPROVED,
    REGEX_DESIGN_FILENAME
} from 'frontier-constants';
import React, {useEffect, useState} from 'react';

export const DisapproveSingleModal = props => {
    const proofingCtx = useProofingContext();

    const {handleDesignDisapproveReason, handleReplaceDesign, handleDesignReview} = proofingCtx;
    const {lastDesignToReviewId, isSubmitting, complete, error} = proofingCtx.data;
    const {designOrder, onReject, children} = props;

    const isReorder = designOrder.is_reorder;

    const [isReadyToUploadNewFile, setReadyToUploadNewFile] = useState(null);
    const [showUploadFileModal, setShowUploadFileModal] = useState(false);
    const [disapproveReasonId, setDisapproveReasonId] = useState(0);
    const [minHeight, setMinHeight] = useState('');

    const isThisLastDesignToReview = lastDesignToReviewId === designOrder.id;
    const replacedDesign = designOrder.replacedDesign || [];
    const disapproveReason = designOrder.disapproveReason;
    const designOrderId = designOrder.id;

    const [trackDesignTrigger, setTrackDesignTrigger] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    const childWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                onClick: () => setIsOpen(true)
            });
        }
        return child;
    });

    let options = getDisapproveReasons(isReorder);

    const handleClose = () => {
        setDisapproveReasonId(null);
        setReadyToUploadNewFile(null);
        setIsOpen(false);
    };

    const handleExpandModal = () => {
        if (minHeight === '') setMinHeight(isReorder ? '200px' : '300px');
        else setMinHeight('');
    };

    const handleResonSelection = val => {
        setShowUploadFileModal(val === DESIGN_DISAPPROVE_REASON_NEW_ARTWORK);
        setReadyToUploadNewFile(null);
        setDisapproveReasonId(val);
    };

    const handleTriggerDesignReview = () => {
        handleDesignReview(designOrderId, DESIGN_STATUS_DISAPPROVED, disapproveReasonId);
        setTrackDesignTrigger(trackDesignTrigger + 1);
        if (!isThisLastDesignToReview) onReject();
    };

    const canRejectArtwork =
        (disapproveReasonId > DESIGN_DISAPPROVE_REASON_NEW_ARTWORK &&
            disapproveReason.length > 4) ||
        replacedDesign.length > 0 ||
        isReadyToUploadNewFile === false;

    useEffect(() => {
        if (isThisLastDesignToReview) {
            if (complete > 0 && !isSubmitting) {
                onReject();
            }
        }
    }, [complete, isSubmitting, trackDesignTrigger]);

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} bodyHeightAuto>
                <ModalHeader>Reject Artwork</ModalHeader>
                <div className="px-0 py-3 disapprove-all-11kt__modal-body">
                    <p className="disapprove-modal-23ll__p">
                        Select a reason for rejection and enter any additional details.
                    </p>
                    <div
                        className="disapprove-all-11kt__modal-select"
                        onClick={handleExpandModal}
                        style={{minHeight}}>
                        <SelectWithErrorHandler
                            className="form-control form-control-sm mb-4"
                            placeholder="Select reason for rejection"
                            onChange={handleResonSelection}
                            errorPath="disapproveReason"
                            value={disapproveReasonId}
                            label="Select a reason"
                            options={options}
                            error={error}
                            newestds
                        />
                    </div>
                    <If
                        condition={
                            disapproveReasonId === DESIGN_DISAPPROVE_REASON_CHANGE_ARTWORK ||
                            disapproveReasonId === DESIGN_DISAPPROVE_REASON_CHANGE_ORDER
                        }>
                        <TextAreaWithErrorHandler
                            onChange={val => {
                                handleDesignDisapproveReason(val, designOrderId);
                            }}
                            className="disapprove-all-11kt__modal-text-area"
                            placeholder="Please tell us why..."
                            errorPath="disapproveReason"
                            value={disapproveReason}
                            error={error}
                        />
                    </If>
                    <If
                        condition={
                            showUploadFileModal &&
                            disapproveReasonId === DESIGN_DISAPPROVE_REASON_NEW_ARTWORK
                        }>
                        <FileUploader
                            closeUploadFileBox={() => setShowUploadFileModal(false)}
                            isReadyToUpload={setReadyToUploadNewFile}
                            pickOptions={{
                                maxFiles: {$set: 1}
                            }}
                            onAllUploadsComplete={newDesign => {
                                setShowUploadFileModal(false);
                                handleReplaceDesign(newDesign, designOrderId);
                            }}
                            thumbnailField="thumb_key"
                            createFileEntity={uploadedFile => {
                                const fileEntity = {
                                    filename: uploadedFile.filename.replace(
                                        REGEX_DESIGN_FILENAME,
                                        ''
                                    ),
                                    key: uploadedFile.key,
                                    filesize: uploadedFile.size,
                                    mimetype: uploadedFile.mimetype,
                                    filestack_id: uploadedFile.handle
                                };
                                return fileEntity;
                            }}
                        />
                    </If>
                    <If condition={replacedDesign.length > 0}>
                        <div className="w-100 d-flex align-items-center justify-content-start gap-2 p-2 rounded bg-structure-lightest b-2">
                            <h5 className="mt-4 d-inline-block">Uploaded: </h5>
                            <span className="ml-2 disapprove-all-11kt__filename">
                                {replacedDesign[0]?.filename}
                            </span>
                            <button className="disapprove-all-11kt__icon-btn p-0 m-0 ms-auto me-0">
                                <i
                                    className="fa fa-trash-o ml-2 color-alert-negative"
                                    onClick={() => {
                                        setReadyToUploadNewFile(null);
                                        handleReplaceDesign({}, designOrderId);
                                    }}
                                />
                            </button>
                        </div>
                    </If>
                </div>
                <ModalFooter>
                    <IfElse condition={isSubmitting}>
                        <div className="d-flex align-items-center justify-content-end">
                            <button className="btn btn-disabled bg-structure-dark color-structure-darkest">
                                <b>Submitting your proof...</b>
                            </button>
                        </div>

                        <div className="d-flex align-items-center justify-content-end gap-4">
                            <button onClick={() => setIsOpen(false)} className="btn btn--secondary">
                                <b>Cancel</b>
                            </button>
                            <IfElse condition={canRejectArtwork}>
                                <button
                                    onClick={handleTriggerDesignReview}
                                    className="btn btn-danger">
                                    <b>Reject Artwork</b>
                                </button>
                                <button
                                    className={
                                        'btn btn-disabled bg-structure-dark color-structure-darkest'
                                    }>
                                    <b>Reject Artwork</b>
                                </button>
                            </IfElse>
                        </div>
                    </IfElse>
                </ModalFooter>
            </Modal>
            <IfElse condition={!!children}>
                <>{childWithProps}</>
                <button>Reject</button>
            </IfElse>
        </>
    );
};
